'use server';

import {
  AdminInitiateAuthCommand,
  AuthFlowType,
  ChallengeNameType,
  CognitoIdentityProvider,
} from '@aws-sdk/client-cognito-identity-provider';
import { cookies } from 'next/headers';

import { signIn } from '@/auth';
import { trackServerAuthEvent } from '@/lib/analytics/server-mixpanel';
import { SIGNUP_IN_PROGRESS_COOKIE } from '@/lib/auth/signup-in-progress-cookie';

function getCognitoClient() {
  return new CognitoIdentityProvider({
    region: process.env.AWS_DEFAULT_REGION ?? '',
    credentials: {
      accessKeyId: process.env.AWS_ACCESS_KEY_ID ?? '',
      secretAccessKey: process.env.AWS_SECRET_ACCESS_KEY ?? '',
    },
  });
}

export type InitiateAuthResult =
  | { kind: 'password_only' }
  | { kind: 'email_otp_required'; session: string; destination: string }
  | { kind: 'new_password_required' }
  | { kind: 'error'; errorName: string; errorMessage: string };

/**
 * サインイン画面で最初に呼ぶ Server Action。
 * AdminInitiateAuth の結果から次のステップ（OTP入力/パスワード設定/ログイン）を識別して返す。
 */
export async function initiateAuthForOtp(args: { email: string; password: string }): Promise<InitiateAuthResult> {
  const { email, password } = args;
  try {
    const cognitoClient = getCognitoClient();
    const command = new AdminInitiateAuthCommand({
      AuthFlow: AuthFlowType.ADMIN_USER_PASSWORD_AUTH,
      ClientId: process.env.NEXT_PUBLIC_COGNITO_CLIENT_ID!,
      UserPoolId: process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID!,
      AuthParameters: { USERNAME: email, PASSWORD: password },
    });
    const response = await cognitoClient.send(command);

    if (response.ChallengeName === ChallengeNameType.EMAIL_OTP && response.Session) {
      const destination = response.ChallengeParameters?.CODE_DELIVERY_DESTINATION ?? '';
      trackServerAuthEvent('Auth_MfaEmailChallengeRequired', undefined, { email });
      return { kind: 'email_otp_required', session: response.Session, destination };
    }
    if (response.ChallengeName === ChallengeNameType.NEW_PASSWORD_REQUIRED) {
      return { kind: 'new_password_required' };
    }
    if (response.AuthenticationResult) {
      return { kind: 'password_only' };
    }
    return {
      kind: 'error',
      errorName: 'UnexpectedChallenge',
      errorMessage: `Unexpected challenge: ${response.ChallengeName ?? 'unknown'}`,
    };
  } catch (error) {
    const errorName = error instanceof Error ? error.name : 'UnknownError';
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error(`[Auth] initiateAuthForOtp 失敗 - email: ${email}, error: ${errorName}: ${errorMessage}`);
    trackServerAuthEvent('Auth_LoginFailed', undefined, { email, errorName, errorMessage });
    return { kind: 'error', errorName, errorMessage };
  }
}

/**
 * OTP メール再送用。AdminInitiateAuth を再実行して新しい Session と宛先を返す。
 * フロントエンド側で 60 秒クールダウンを担保する想定。
 */
export async function resendOtp(args: {
  email: string;
  password: string;
}): Promise<
  { success: true; session: string; destination: string } | { success: false; errorName: string; errorMessage: string }
> {
  const result = await initiateAuthForOtp(args);
  if (result.kind === 'email_otp_required') {
    return { success: true, session: result.session, destination: result.destination };
  }
  if (result.kind === 'error') {
    return { success: false, errorName: result.errorName, errorMessage: result.errorMessage };
  }
  return {
    success: false,
    errorName: 'MfaNotRequired',
    errorMessage: `MFA が要求されていません（kind: ${result.kind}）`,
  };
}

export async function login(
  formData: {
    email: string;
    password: string;
    otpCode?: string;
    cognitoSession?: string;
    redirectTo?: string;
    redirect?: boolean;
  },
  provider?: 'credentials',
) {
  try {
    await signIn(provider, formData);
  } catch (error) {
    throw error;
  }
}

/**
 * サインアップフロー専用の signIn。
 * NEW_PASSWORD_REQUIRED ユーザー向けの一時セッション確立のみ auth.config で許可する。
 */
export async function loginForSignup(
  formData: {
    email: string;
    password: string;
    newPassword?: string;
    redirect?: boolean;
  },
  provider?: 'credentials',
) {
  const signupInternalToken = process.env.AUTH_SIGNUP_INTERNAL_TOKEN;
  if (!signupInternalToken) {
    throw new Error('AUTH_SIGNUP_INTERNAL_TOKEN is not configured');
  }

  try {
    await signIn(provider, { ...formData, signupInternalToken });
  } catch (error) {
    throw error;
  }
}

export async function clearSignupInProgressCookie() {
  const cookieStore = await cookies();
  cookieStore.delete(SIGNUP_IN_PROGRESS_COOKIE);
}

// NOTE: 旧 logout() server action は削除した。
// 理由: server action 経由の signOut は jwt callback と race condition があり、
// session cookie が新しい token で上書きされて削除されないバグがあった。
// ログアウトは next-auth/react の signOut を client side で呼ぶ形に統一したため不要。
// 詳細: PR #2527
